import { profileFetch } from "../profileSession";
import { API_BASE, getToken, req, downloadApiFile } from "./transport";
import { MAX_LISTING_PHOTO_BYTES, LISTING_PHOTO_SIZE_ERROR } from "../listingPhotos";
import type { ItemPostingRule, ArrangementQuoteInput, ArrangementCheckout, Order, AgreementDocument, PurchaseOfferReview, PurchaseCheckoutInput } from "./types";

export const itemsApi = {
  async purchaseOfferRequirements(itemId?: string) { return req<{ active: boolean; fields: string[]; missingFacts?: string[] }>(`/items/purchase-requirements${itemId ? `?itemId=${encodeURIComponent(itemId)}` : ""}`); },
  async previewPurchaseListing(payload: Record<string, unknown>) { return req<{ document: AgreementDocument | null }>("/items/purchase-preview", { method: "POST", body: JSON.stringify(payload) }); },
  async previewPrivatePurchase(requestId: string, action: "accept" | "offer", purchaseReview: PurchaseOfferReview, proposal?: ArrangementQuoteInput) {
    return req<{ document: AgreementDocument | null }>(`/item-arrangements/${encodeURIComponent(requestId)}/purchase-preview`, { method: "POST", body: JSON.stringify({ action, purchaseReview, proposal }) });
  },
  async previewPurchaseCheckout(payload: PurchaseCheckoutInput) { return req<{ document: AgreementDocument | null; sellerOfferDocument: AgreementDocument | null; order?: Order }>("/orders/preview", { method: "POST", body: JSON.stringify(payload) }); },
  async downloadPurchaseAgreement(orderId: string) { return downloadApiFile(`/orders/${encodeURIComponent(orderId)}/agreement.pdf`, `purchase-${orderId}.pdf`, "Could not download the complete purchase agreement. Retry."); },
  // [marketplace.sorts-and-ratings](../../../../docs/product/marketplace.md#marketplace-sorts-and-ratings): item discovery filters seller reputation before paging;
  // callers must carry minRating and repeated itemTypes into continuation requests.
  async listItemsPage(params?: { q?: string; kind?: string; mode?: string; category?: string; city?: string; itemType?: string; itemTypes?: string[]; minRating?: number; sort?: string; cursor?: string; limit?: number }, signal?: AbortSignal) {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params || {})) {
      if (Array.isArray(v)) v.forEach((value) => qs.append(k, value));
      else if (v !== undefined && v !== "") qs.set(k, String(v));
    }
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    // A manual read can be aborted and must report expired access as a failure.
    const page = await req<{ items: any[]; total: number; nextCursor?: string | null }>(`/items${suffix}`, { signal }, { rejectUnauthorized: signal !== undefined });
    if (signal && !page) throw new Error("Missing marketplace items page.");
    return page ?? { items: [], total: 0, nextCursor: null };
  },

  async listItems(params?: { q?: string; kind?: string; mode?: string; category?: string }, signal?: AbortSignal) {
    const page = await this.listItemsPage(params, signal);
    if (signal && !Array.isArray(page?.items)) throw new Error("Missing marketplace items.");
    return page.items;
  },

  async getItem(id: string) {
    const r = await req<{ item: any }>(`/items/${encodeURIComponent(id)}`);
    return r?.item ?? null;
  },

  async itemPostingCatalog() {
    return req<{ items: ItemPostingRule[] }>("/items/catalog");
  },

  async createItem(payload: Record<string, unknown>) {
    return req<{ item: any }>("/items", { method: "POST", body: JSON.stringify(payload) });
  },

  async myItems(signal?: AbortSignal) {
    const r = await req<{ items: any[] }>("/items/mine", { signal }, { rejectUnauthorized: signal !== undefined });
    if (signal && !Array.isArray(r?.items)) throw new Error("Missing owned items.");
    return r?.items ?? [];
  },

  async updateItem(id: string, payload: Record<string, unknown>) {
    return req<{ item: any }>(`/items/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(payload) });
  },

  async archiveItem(id: string) {
    return req<{ item: any }>(`/items/${encodeURIComponent(id)}`, { method: "DELETE" });
  },

  async deleteItem(id: string) {
    return req<{ ok: boolean; deleted: string }>(`/items/${encodeURIComponent(id)}/permanent`, { method: "DELETE" });
  },

  async uploadItemImage(id: string, file: File) {
    const token = getToken();
    const res = await profileFetch(`${API_BASE}/items/${encodeURIComponent(id)}/image`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": file.type || "application/octet-stream",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: file,
    });
    const data = await res.json();
    if (!res.ok) throw new Error((data as any)?.error || `Upload failed (${res.status})`);
    return data as { item: any };
  },

  itemImageUrl(id: string) {
    return `${API_BASE}/items/${encodeURIComponent(id)}/image`;
  },

  // v3.9.6 — post gallery images (item | service | job | project)
  async postImages(type: string, id: string) {
    const r = await req<{ images: { id: string; url: string; orderIndex: number }[] }>(`/post-images/${type}/${encodeURIComponent(id)}`);
    return r?.images ?? [];
  },

  postImageSrc(type: string, id: string, imgId: string) {
    return `${API_BASE}/post-images/${type}/${encodeURIComponent(id)}/${imgId}`;
  },

  async uploadPostImage(type: string, id: string, blob: Blob) {
    if (blob.size > MAX_LISTING_PHOTO_BYTES) throw new Error(LISTING_PHOTO_SIZE_ERROR);
    const token = getToken();
    const res = await profileFetch(`${API_BASE}/post-images/${type}/${encodeURIComponent(id)}`, {
      method: "POST", credentials: "include",
      headers: { "Content-Type": (blob as any).type || "image/jpeg", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: blob,
    });
    const data = await res.json();
    if (!res.ok) throw new Error((data as any)?.error || `Upload failed (${res.status})`);
    return data as { image: any };
  },

  async deletePostImage(type: string, id: string, imgId: string) {
    return req<{ ok: boolean }>(`/post-images/${type}/${encodeURIComponent(id)}/${imgId}`, { method: "DELETE" });
  },

  async requestItemArrangement(payload: ArrangementQuoteInput & { itemId: string; qty: number }) {
    return req<{ threadId: string }>("/item-arrangements", { method: "POST", body: JSON.stringify(payload) });
  },

  async sendItemArrangementQuote(requestId: string, payload: ArrangementQuoteInput) {
    return req<{ ok: boolean }>(`/item-arrangements/${encodeURIComponent(requestId)}/quotes`, { method: "POST", body: JSON.stringify(payload) });
  },

  /** Agrees to the saved buyer request without payment. Seller-only; rejects a
   * missing price or pending counterproposal and preserves terminal replay. */
  async acceptItemArrangementRequest(requestId: string, purchaseReview?: PurchaseOfferReview) {
    return req<{ ok: boolean }>(`/item-arrangements/${encodeURIComponent(requestId)}/accept`, { method: "POST", body: JSON.stringify({ purchaseReview }) });
  },

  async decideItemArrangement(quoteId: string, decision: "accept" | "changes", note = "", proposal?: ArrangementQuoteInput, expectedDocumentId?: string) {
    return req<{ ok: boolean }>(`/item-arrangements/quotes/${encodeURIComponent(quoteId)}/${decision}`, { method: "POST", body: JSON.stringify({ note, proposal, expectedDocumentId }) });
  },

  async getItemArrangementQuote(quoteId: string) {
    return req<{ quote: ArrangementCheckout }>(`/item-arrangements/quotes/${encodeURIComponent(quoteId)}`);
  },

  async placeOrder(payload: PurchaseCheckoutInput) {
    return req<{ order: Order }>("/orders", { method: "POST", body: JSON.stringify(payload) });
  },

  async myOrders(signal?: AbortSignal) {
    return req<{ purchases: Order[]; sales: Order[] }>("/orders/mine", { signal }, { rejectUnauthorized: signal !== undefined });
  },

  async order(id: string) {
    return req<{ order: Order }>(`/orders/${encodeURIComponent(id)}`);
  },

  async fulfillOrder(id: string) {
    return req<{ order: any }>(`/orders/${encodeURIComponent(id)}/fulfill`, { method: "POST" });
  },

  async confirmOrder(id: string, milestoneId?: string) {
    return req<{ order: Order }>(`/orders/${encodeURIComponent(id)}${milestoneId ? `/milestones/${encodeURIComponent(milestoneId)}` : ""}/confirm`, { method: "POST" });
  },

  async cancelOrder(id: string) {
    return req<{ order: any }>(`/orders/${encodeURIComponent(id)}/cancel`, { method: "POST" });
  }
};
